import { Component, OnInit, Inject, ViewChild, Input, Optional } from '@angular/core';
import { ParserService } from '@icc/b2b/shared';
import { PositionsDetailsService } from '@icc/b2b/shared';
import { MatTreeNestedDataSource, MatTree } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { StateService } from '@icc/shared/state.service';
import { ConfigurationsService } from '@icc/configurator/shared';
import { TranslateService } from '@icc/shared/translate.service';
import { UserService } from '@icc/shared/user.service';
import { ConfiguratorFacade } from '@icc/configurator/shared';
import { SharedFacade } from '@icc/configurator/shared';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DoorActiveConfiguration, ComponentsServiceBase, ComponentsState } from '@icc/window-door/types';
import { Components } from '@icc/window-door/types';


@Component({
    selector: 'icc-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    standalone: false
})
export class DetailsComponent implements OnInit {
    @Input() children: any;
    @ViewChild('tree') tree!: MatTree<any>;

    details = {};
    priceElementsVisible = false;
    showParametersModal = false;
    showPriceDetails = false;
    currency = {};
    price = {};
    hiddenPrice = false;
    showSystemsComparision = false;
    treeControl = new NestedTreeControl<any>(node => node.children);
    dataSource = new MatTreeNestedDataSource<any>();
    detailsTree: any = [];
    private unsubscribe$ = new Subject<void>();
    price$ = this.configuratorFacade.price$;
    customConstructionName: string | undefined;
    quantity = this.configurationsService.getQuantity();
    showOfferDataInputs: boolean;
    returnButton = '';
    components: Components = {
        fullDoor: true,
        activeSash: true,
        passiveSash: true,
        frame: true,
        threshold: true,
        hardware: true,
        sideLight: {
            left: true,
            right: true,
            top: true,
        },
        extension: {
            left: true,
            right: true,
            top: true,
        },
        bottomExtension: true,
        portal: true,
        casing: true,
        accessories: true,
        pull: true,
        recessedPull: true,
    };
    componentsState: ComponentsState | null = null;
    user =  {};

    componentsList = [
        { path: 'activeSash', labelKey: /* i18nextract */'DOOR|Skrzydło drzwiowe' },
        { path: 'passiveSash', labelKey: /* i18nextract */'DOOR|Skrzydło bierne' },
        { path: 'frame', labelKey: /* i18nextract */'DOOR|Ościeżnica' },
        { path: 'threshold', labelKey: /* i18nextract */'DOOR|Próg' },
        { path: 'hardware', labelKey: /* i18nextract */'DOOR|Osprzęt' },
        { path: 'sideLight.left', labelKey: /* i18nextract */'DOOR|Doświetlenie lewe' },
        { path: 'sideLight.right', labelKey: /* i18nextract */'DOOR|Doświetlenie prawe' },
        { path: 'sideLight.top', labelKey: /* i18nextract */'DOOR|Doświetlenie górne' },
        { path: 'extension.left', labelKey: /* i18nextract */'DOOR|Poszerzenie lewe' },
        { path: 'extension.right', labelKey: /* i18nextract */'DOOR|Poszerzenie prawe' },
        { path: 'extension.top', labelKey: /* i18nextract */'DOOR|Poszerzenie górne' },
        { path: 'bottomExtension', labelKey: /* i18nextract */'DOOR|Podwalina' },
        { path: 'portal', labelKey: /* i18nextract */'DOOR|Portal' },
        { path: 'casing', labelKey: /* i18nextract */'DOOR|Obejma' },
        { path: 'accessories', labelKey: /* i18nextract */'DOOR|Dodatki do konstrukcji' },
        { path: 'pull', labelKey: /* i18nextract */'DOOR|Pochwyt' },
        { path: 'recessedPull', labelKey: /* i18nextract */'DOOR|Pochwyt kieszeniowy' }
    ];

    componentsAvailable = false;

    constructor(
        @Inject(APP_CONFIG) public config: AppConfigFactory,
        private stateService: StateService,
        private configurationsService: ConfigurationsService,
        private translateService: TranslateService,
        private userService: UserService,
        private configuratorFacade: ConfiguratorFacade,
        private sharedFacade: SharedFacade,
        @Optional() private componentsService?: ComponentsServiceBase,
    ) {
        // Inicjalizacja stanu komponentów dla konfiguratora drzwi
        if (this.componentsService) {
            this.componentsState = this.componentsService.getCurrentState();
        }

        this.sharedFacade.currency$.pipe(takeUntil(this.unsubscribe$)).subscribe(currency => {
            this.currency = currency;
        });

        this.configurationsService.conf$.subscribe(data=>{
            this.customConstructionName = data.Current?.Title;
            this.quantity = data.Current?.Quantity || 1;
            if(DoorActiveConfiguration.is(data.Current)) {
                this.componentsAvailable = (config().preset === 'b2b'
                    && config().componentModule
                    && data.Current?.System.door_components_available);
            }
        });
        this.configuratorFacade.price$.pipe(takeUntil(this.unsubscribe$)).subscribe(price => {
            this.price = this.quantity ? price.priceAfterDiscountsInCurrency*this.quantity : price.priceAfterDiscountsInCurrency;
        });

        this.sharedFacade.returnButton$.pipe(takeUntil(this.unsubscribe$)).subscribe(button => {
            this.returnButton = button;
        });

        if (this.componentsService) {
            this.componentsService.componentsState$.pipe(takeUntil(this.unsubscribe$)).subscribe((state: ComponentsState) => {
                this.componentsState = state;
            });
        }

        this.showOfferDataInputs =
            (this.config().preset === 'b2b' &&
                !this.config().IccConfig.Configurators
                    .configurationQuantityWithButtonsAndCustomName) ||
            this.config().IccConfig.Configurators.configurationQuantityWithButtonsAndCustomName;

        this.configurationsService.conf$.subscribe(data=>{
            if(DoorActiveConfiguration.is(data.Current)) {
                this.components = data.Current.Components || {
                    fullDoor: false,
                    activeSash: false,
                    passiveSash: false,
                    frame: false,
                    threshold: false,
                    hardware: false,
                    sideLight: { left: false, right: false, top: false },
                    extension: { left: false, right: false, top: false },
                    bottomExtension: false,
                    portal: false,
                    casing: false,
                    accessories: false,
                    pull: false,
                    recessedPull: false
                };
            }
        });
    }

    onNameChange(value: string){
        this.configurationsService.setConstructionName(value);
    }

    onQuantityChange(value: number){
        if (Array.from(value.toString()).every(p => p === '0')) {
            this.configurationsService.setQuantity(1)
        }
        this.configurationsService.setQuantity(value);
    }

    increaseQuantity() {
        let quantity = Number(this.configurationsService.getQuantity());
        this.configurationsService.setQuantity(quantity += 1);
    }

    decreaseQuantity() {
        let quantity = Number(this.configurationsService.getQuantity());
        this.configurationsService.setQuantity(quantity -= 1);
    }

    onComponentChange(path: string, value: boolean) {
        const keys = path.split('.');
        let current: any = this.components;

        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }

        if (value === false && !this.canUncheckComponent()) {
            return;
        }

        current[keys[keys.length - 1]] = value;

        if (path === 'fullDoor' && value === true) {
            this.components.activeSash = true;
            this.components.passiveSash = true;
            this.components.frame = true;
            this.components.threshold = true;
            this.components.hardware = true;
            this.components.sideLight.left = true;
            this.components.sideLight.right = true;
            this.components.sideLight.top = true;
            this.components.extension.left = true;
            this.components.extension.right = true;
            this.components.extension.top = true;
            this.components.bottomExtension = true;
            this.components.portal = true;
            this.components.casing = true;
            this.components.accessories = true;
            this.components.pull = true;
            this.components.recessedPull = true;
        }

        if (DoorActiveConfiguration.is(this.configurationsService.conf.Current)) {
            this.configurationsService.conf.Current.Components = this.components;
        }

        if (this.componentsService) {
            this.componentsService.updateComponentsFromConfiguration(this.components);
        }
        this.loadDetails(this.user)

    }

    private canUncheckComponent(): boolean {
        const checkedCount = this.getCheckedComponentsCount();

        if (checkedCount <= 1) {
            return false;
        }

        return true;
    }

    public getCheckedComponentsCount(): number {
        const flattenComponentsState = (obj: any, prefix = ''): Array<{key: string, state: any}> => {
            const result: Array<{key: string, state: any}> = [];

            Object.entries(obj).forEach(([key, value]) => {
                const fullKey = prefix ? `${prefix}.${key}` : key;

                if (value && typeof value === 'object' && 'available' in value) {
                    result.push({ key: fullKey, state: value });
                } else if (value && typeof value === 'object') {
                    const nested = flattenComponentsState(value, fullKey);
                    for (const item of nested) {
                        result.push(item);
                    }
                }
            });

            return result;
        };

        const getComponentValue = (path: string): boolean => {
            const keys = path.split('.');
            let current: any = this.components;

            for (const k of keys) {
                current = current?.[k];
            }

            return current === true;
        };

        return flattenComponentsState(this.componentsState)
            .filter(({ key, state }) => state.available && getComponentValue(key))
            .length;
    }

    isComponentDisabled(path: string): boolean {
        if (this.components.fullDoor && path !== 'fullDoor') {
            return true;
        }

        if (path !== 'fullDoor' && this.getCheckedComponentsCount() <= 1) {
            const keys = path.split('.');
            let current: any = this.components;

            for (let i = 0; i < keys.length - 1; i++) {
                current = current[keys[i]];
            }

            return current[keys[keys.length - 1]] === true;
        }

        return false;
    }

    isComponentAvailable(path: string): boolean {
        const keys = path.split('.');
        let current: any = this.componentsState;

        for (const key of keys) {
            current = current?.[key];
        }

        return current?.available === true;
    }

    getComponentValue(path: string): boolean {
        const keys = path.split('.');
        let current: any = this.components;

        for (const key of keys) {
            current = current?.[key];
        }

        return current === true;
    }

    ngOnInit() {
        this.hiddenPrice = this.config().preset === 'b2c' && this.config().hidePricesInB2cConfigurator;

        const currentConfig = this.configurationsService.conf.Current;
        if (this.componentsService && DoorActiveConfiguration.is(currentConfig) && currentConfig.Components) {
            this.componentsService.updateComponentsFromConfiguration(currentConfig.Components);
        }

        this.user =  this.userService.get();
        this.loadDetails(this.user);
    }

    loadDetails(user) {
        const positions: any = new PositionsDetailsService(
            this.config().IccConfig,
            [
                {
                    configuration: this.configurationsService.conf.Current,
                    details: this.configurationsService.createSimpleConfiguration(
                        this.configurationsService.conf.Current
                    ),
                    summary: {
                        dealer: {
                            components: {},
                        },
                        client: {
                            components: {},
                        },
                    },
                    group_discounts: {},
                    confType: this.configurationsService.conf.Current.type
                },
            ],
            this.stateService.getCurrentOffer(),
            user.access,
            true,
            user.access,
            this.config().CurLang,
            this.translateService.instant.bind(this.translateService),
            false,
            this.config().showTimeLimitInDetails,
            false,
            false,
            null,
            false,
            null,
            false,
            typeof this.config === 'function' ? this.config() : this.config
        );
        positions[0].detailsForView.allWidth = true;
        this.detailsTree = positions[0].detailsTree;
        const data = this.convertToTreeData(JSON.parse(JSON.stringify(this.detailsTree)));
        this.dataSource.data = data;
        this.treeControl.dataNodes = data;
        this.treeControl.expandAll();
    }

    hasChild = (_: number, node: any) => !!node.children && node.children.length > 0;

    private convertToTreeData(tree: any) {
        if (tree.$$children) {
            tree.children = tree.$$children;
            tree.children.forEach(node => {
                this.convertToTreeData(node);
            });
            delete tree.$$children;
        }
        return tree.children;
    }

    ngOnDestroy(){
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
